<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { getTemplateWithExercises, saveTemplateWithExercises } from '../services/templates.js'
  import { createDefaultSet } from '../services/db.js'
  import ExerciseSelector from './ExerciseSelector.svelte'

  export let mode = 'create' // 'create' | 'edit'
  export let templateId = null

  const dispatch = createEventDispatcher()

  let template = {
    id: null,
    name: '',
    description: ''
  }

  let exercises = []
  let isSubmitting = false
  let draggedIndex = null

  onMount(async () => {
    if (mode === 'edit' && templateId) {
      try {
        const data = await getTemplateWithExercises(templateId)
        if (data) {
          template = {
            id: data.template.id,
            name: data.template.name,
            description: data.template.description || ''
          }
          exercises = data.exercises.map(ex => ({
            id: ex.id,
            exercise_name: ex.exercise_name,
            sets: Array.isArray(ex.sets) ? ex.sets : [],
            rest_time: ex.rest_time || '',
            notes: ex.notes || ''
          }))
        }
      } catch (error) {
        console.error('Failed to load template:', error)
        alert('無法載入模板')
        dispatch('close')
      }
    }
  })

  function handleClose() {
    if (hasUnsavedChanges()) {
      if (!confirm('有尚未儲存的變更，確定要離開嗎？')) {
        return
      }
    }
    dispatch('close')
  }

  function hasUnsavedChanges() {
    if (mode === 'create') {
      return template.name || template.description || exercises.length > 0
    }
    return true
  }

  function addExercise() {
    exercises = [...exercises, {
      id: crypto.randomUUID(),
      exercise_name: '',
      sets: [createDefaultSet()],
      rest_time: '',
      notes: ''
    }]
  }

  function removeExercise(index) {
    const exercise = exercises[index]
    if (exercise && exercise.exercise_name) {
      if (!confirm(`確定要移除「${exercise.exercise_name}」嗎？`)) {
        return
      }
    }
    exercises = exercises.filter((_, i) => i !== index)
  }

  function handleExerciseSelect(event, index) {
    exercises[index].exercise_name = event.detail.name
    exercises = exercises
  }

  function addSet(exerciseIndex) {
    const exercise = exercises[exerciseIndex]
    const lastSet = exercise.sets[exercise.sets.length - 1]
    exercise.sets = [...exercise.sets, createDefaultSet(lastSet)]
    exercises = exercises
  }

  function removeSet(exerciseIndex, setIndex) {
    if (exercises[exerciseIndex].sets.length <= 1) return
    exercises[exerciseIndex].sets = exercises[exerciseIndex].sets.filter((_, i) => i !== setIndex)
    exercises = exercises
  }

  // Drag and drop handlers
  function handleDragStart(index) {
    draggedIndex = index
  }

  function handleDragOver(event, index) {
    event.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newExercises = [...exercises]
    const draggedItem = newExercises[draggedIndex]
    newExercises.splice(draggedIndex, 1)
    newExercises.splice(index, 0, draggedItem)

    draggedIndex = index
    exercises = newExercises
  }

  function handleDragEnd() {
    draggedIndex = null
  }

  async function handleSubmit() {
    if (isSubmitting) return

    // Validate
    if (!template.name?.trim()) {
      alert('請輸入模板名稱')
      return
    }

    // Filter exercises with names
    const validExercises = exercises
      .filter(ex => ex.exercise_name?.trim())
      .map((ex, index) => ({
        id: ex.id,
        exercise_name: ex.exercise_name.trim(),
        order_index: index,
        sets: Array.isArray(ex.sets)
          ? ex.sets.map(set => ({
              weight: set.weight ? parseFloat(set.weight) : null,
              reps: set.reps ? parseInt(set.reps, 10) : null,
              completed: false
            }))
          : [],
        rest_time: ex.rest_time ? parseInt(ex.rest_time, 10) : null,
        notes: ex.notes?.trim() || ''
      }))

    isSubmitting = true

    try {
      const templateData = {
        id: template.id,
        name: template.name.trim(),
        description: template.description?.trim() || ''
      }

      await saveTemplateWithExercises(templateData, validExercises)

      // Show success toast
      showToast(mode === 'create' ? '模板已儲存' : '模板已更新')

      setTimeout(() => {
        dispatch('save')
      }, 500)

    } catch (error) {
      console.error('Save error:', error)
      alert('儲存失敗，請稍後再試')
      isSubmitting = false
    }
  }

  function showToast(message) {
    const toast = document.createElement('div')
    toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in'
    toast.textContent = `✓ ${message}`
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2000)
  }

  $: descriptionCount = template.description?.length || 0
</script>

<div class="template-editor-container fixed inset-0 bg-white z-50 overflow-y-auto">
  <div class="max-w-2xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">
        {mode === 'create' ? '新增訓練模板' : '編輯訓練模板'}
      </h1>
      <button
        type="button"
        class="text-gray-600 hover:text-gray-800 text-2xl font-bold"
        on:click={handleClose}
      >
        ✕
      </button>
    </div>

    <!-- Form -->
    <form on:submit|preventDefault={handleSubmit} class="space-y-6">
      <!-- Basic Info -->
      <div class="card">
        <h2 class="text-xl font-semibold mb-4 text-gray-800">基本資訊</h2>

        <div class="space-y-4">
          <!-- Template Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              模板名稱 <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              class="input-field w-full"
              placeholder="例如：胸背訓練、腿部訓練..."
              bind:value={template.name}
              maxlength="100"
              required
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              描述
            </label>
            <textarea
              class="input-field w-full"
              rows="3"
              placeholder="模板說明、適用時機等..."
              maxlength="500"
              bind:value={template.description}
            ></textarea>
            <div class="text-sm text-gray-500 mt-1">
              {descriptionCount} / 500 字元
            </div>
          </div>
        </div>
      </div>

      <!-- Exercises Section -->
      <div class="card">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold text-gray-800">運動項目</h2>
          <span class="text-sm text-gray-500">可拖曳排序</span>
        </div>

        <div class="space-y-4">
          {#if exercises.length === 0}
            <p class="text-gray-500 text-center py-4">
              尚未新增運動項目，點擊下方按鈕新增
            </p>
          {:else}
            {#each exercises as exercise, index (exercise.id)}
              <div
                class="exercise-card bg-white border border-gray-200 rounded-lg p-4 shadow-sm cursor-move"
                draggable="true"
                on:dragstart={() => handleDragStart(index)}
                on:dragover={(e) => handleDragOver(e, index)}
                on:dragend={handleDragEnd}
                class:opacity-50={draggedIndex === index}
              >
                <div class="flex justify-between items-start mb-3">
                  <div class="flex items-center gap-2">
                    <span class="text-gray-400">≡</span>
                    <h3 class="text-lg font-semibold text-gray-700">運動 {index + 1}</h3>
                  </div>
                  <button
                    type="button"
                    class="text-red-600 hover:text-red-800 font-medium"
                    on:click={() => removeExercise(index)}
                  >
                    移除
                  </button>
                </div>

                <div class="space-y-3">
                  <!-- Exercise Name -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      運動名稱 <span class="text-red-500">*</span>
                    </label>
                    <ExerciseSelector
                      selectedName={exercise.exercise_name}
                      on:select={(e) => handleExerciseSelect(e, index)}
                    />
                  </div>

                  <!-- Rest Time -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      組間休息時間
                    </label>
                    <div class="flex items-center gap-2">
                      <input
                        type="number"
                        class="input-field flex-1"
                        placeholder="90"
                        bind:value={exercise.rest_time}
                        min="0"
                        max="3600"
                      />
                      <span class="text-sm text-gray-500">秒</span>
                    </div>
                    <div class="flex gap-2 mt-2">
                      <button
                        type="button"
                        class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
                        on:click={() => exercise.rest_time = 60}
                      >
                        60s
                      </button>
                      <button
                        type="button"
                        class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
                        on:click={() => exercise.rest_time = 90}
                      >
                        90s
                      </button>
                      <button
                        type="button"
                        class="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600"
                        on:click={() => exercise.rest_time = 120}
                      >
                        120s
                      </button>
                    </div>
                  </div>

                  <!-- Sets -->
                  <div>
                    <div class="flex justify-between items-center mb-2">
                      <span class="text-sm font-medium text-gray-700">組數設定</span>
                      <span class="text-xs text-gray-500">預設值可留空</span>
                    </div>
                    <div class="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 mb-2 px-1">
                      <div class="col-span-2">SET</div>
                      <div class="col-span-5">KG</div>
                      <div class="col-span-5">REPS</div>
                    </div>

                    <div class="space-y-2">
                      {#each exercise.sets as set, setIndex}
                        <div class="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded-lg">
                          <div class="col-span-2 text-sm font-medium text-gray-600">
                            {setIndex + 1}
                          </div>
                          <div class="col-span-5">
                            <input
                              type="number"
                              class="input-field w-full text-sm py-1"
                              placeholder="留空"
                              bind:value={set.weight}
                              min="0"
                              max="9999"
                              step="0.5"
                            />
                          </div>
                          <div class="col-span-4">
                            <input
                              type="number"
                              class="input-field w-full text-sm py-1"
                              placeholder="留空"
                              bind:value={set.reps}
                              min="0"
                              max="999"
                            />
                          </div>
                          <div class="col-span-1 flex justify-center">
                            {#if exercise.sets.length > 1}
                              <button
                                type="button"
                                class="w-6 h-6 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 text-sm flex items-center justify-center"
                                on:click={() => removeSet(index, setIndex)}
                                title="刪除此組"
                              >
                                ×
                              </button>
                            {/if}
                          </div>
                        </div>
                      {/each}
                    </div>

                    <button
                      type="button"
                      class="w-full mt-3 py-2 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg border border-dashed border-primary-300"
                      on:click={() => addSet(index)}
                    >
                      + 新增一組
                    </button>
                  </div>

                  <!-- Notes -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      備註
                    </label>
                    <textarea
                      class="input-field w-full text-sm"
                      rows="2"
                      placeholder="技巧提示、注意事項..."
                      maxlength="200"
                      bind:value={exercise.notes}
                    ></textarea>
                  </div>
                </div>
              </div>
            {/each}
          {/if}
        </div>

        <button
          type="button"
          class="btn btn-secondary w-full mt-4"
          on:click={addExercise}
        >
          + 新增運動項目
        </button>
      </div>

      <!-- Form Actions -->
      <div class="sticky bottom-0 bg-white border-t border-gray-200 py-4 -mx-4 px-4">
        <div class="flex gap-4 max-w-2xl mx-auto">
          <button
            type="button"
            class="btn btn-secondary flex-1"
            on:click={handleClose}
          >
            取消
          </button>
          <button
            type="submit"
            class="btn btn-primary flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? '儲存中...' : (mode === 'create' ? '儲存' : '更新')}
          </button>
        </div>
      </div>
    </form>
  </div>
</div>

<style>
  .exercise-card {
    transition: opacity 0.2s;
  }
</style>
